import { getBaseUrl } from "../../../utils/requestUtils";
import { fetchOrLogout } from "../../../utils/helpers";

export interface GlobalSearchResult {
  sfid: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  due_date: string | null;
  city: string | null;
  state: string | null;
  physician_name: string | null;
  medical_record_number: string | null;
  is_affiliated: boolean;
}

export const getAttestationState = async (
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<{ attested_at: string | null; is_valid: boolean } | null> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/global_search/attest`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      },
      setLoggedOut,
    );
    if (!response.ok) return null;
    return await response.json();
  } catch (e) {
    return null;
  }
};

export type GlobalSearchError = "blocked" | "network";

const GLOBAL_SEARCH_ACCESS_SUSPENDED_CODE = "global_search_access_suspended";

const getGlobalSearchError = async (
  response: Response,
): Promise<GlobalSearchError> => {
  if (response.status !== 403) return "network";
  try {
    const data = (await response.json()) as { code?: unknown };
    return data.code === GLOBAL_SEARCH_ACCESS_SUSPENDED_CODE
      ? "blocked"
      : "network";
  } catch {
    return "network";
  }
};

export const searchGlobalPatients = async (
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
  firstName: string,
  lastName: string,
  dob: string,
): Promise<GlobalSearchResult[] | GlobalSearchError> => {
  try {
    const baseUrl = await getBaseUrl();
    const params = new URLSearchParams({
      first_name: firstName,
      last_name: lastName,
      date_of_birth: dob,
    });
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/global_search?${params}`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      },
      setLoggedOut,
    );
    if (!response.ok) return await getGlobalSearchError(response);
    return await response.json();
  } catch (e) {
    return "network";
  }
};

export interface GlobalSearchAccount {
  sfid: string;
  sfname: string;
}

export const getProviderAccounts = async (
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<GlobalSearchAccount[] | null> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/global_search/accounts`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      },
      setLoggedOut,
    );
    if (!response.ok) return null;
    return await response.json();
  } catch (e) {
    console.error("Failed to fetch global search accounts", e);
    return null;
  }
};

export const affiliateRequisition = async (
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
  reqSfid: string,
  accountSfid: string,
): Promise<boolean> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/global_search/affiliate/${reqSfid}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ account_sfid: accountSfid }),
      },
      setLoggedOut,
    );
    return response.ok;
  } catch (e) {
    return false;
  }
};

export const disaffiliateRequisition = async (
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
  reqSfid: string,
): Promise<boolean> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/global_search/affiliate/${reqSfid}`,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      },
      setLoggedOut,
    );
    return response.ok;
  } catch (e) {
    return false;
  }
};

export const attestGlobalSearch = async (
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<void> => {
  try {
    const baseUrl = await getBaseUrl();
    await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/global_search/attest`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      },
      setLoggedOut,
    );
  } catch {
    // Attestation failure is non-blocking
  }
};
